/**
 * Live-load Google Fonts by family name so brand fonts render as the real
 * typeface in previews (instead of silently falling back to a system font).
 *
 * Injects a single <link rel="stylesheet" href="fonts.googleapis.com/css2?..">
 * into <head> per family, idempotently. The platform CSP already allows
 * fonts.googleapis.com / fonts.gstatic.com (see main.py security headers).
 *
 * NOTE: we deliberately request the bare family (default 400 weight) with no
 * `:wght@..` axis. The css2 API returns HTTP 400 — breaking the whole
 * stylesheet — if you request a weight a static font doesn't ship, so for a
 * robust "any brand font" preview we let Google serve the default face.
 */

// Web-safe / system families that are NOT on Google Fonts — never try to fetch.
// Mirrors the backend exclusion lists (landing_page_routes.py, renderer.py).
const SYSTEM_FONTS = new Set<string>([
  'arial', 'helvetica', 'georgia', 'times', 'times new roman', 'verdana',
  'trebuchet', 'trebuchet ms', 'courier', 'courier new', 'palatino', 'garamond',
  'tahoma', 'geneva', 'sans-serif', 'serif', 'monospace', 'system-ui',
  '-apple-system', 'blinkmacsystemfont', 'segoe ui', 'roboto',
]);

// Track which families we've already injected (per page session).
const loaded = new Set<string>();

/** Extract the first family name from a CSS font-family value ("Lora, serif" -> "Lora"). */
const firstFamily = (value?: string | null): string => {
  if (!value) return '';
  return value.split(',')[0].trim().replace(/['"]/g, '');
};

/**
 * Ensure a Google Font is loaded for the given font-family value(s).
 * System / web-safe fonts and blanks are skipped. Safe to call repeatedly.
 */
export const ensureGoogleFont = (...values: Array<string | null | undefined>): void => {
  if (typeof document === 'undefined') return;
  for (const value of values) {
    const family = firstFamily(value);
    if (!family) continue;
    const key = family.toLowerCase();
    if (SYSTEM_FONTS.has(key) || loaded.has(key)) continue;
    loaded.add(key);

    const href =
      `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}&display=swap`;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-google-font', key);
    document.head.appendChild(link);
  }
};
